import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import axios from 'axios';
import { getMe, logout, type AuthUser } from '@/api/auth';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const isInitialized = ref(false);

  const initialize = async () => {
    if (isInitialized.value) {
      return;
    }
    try {
      user.value = await getMe();
    } catch (error) {
      if (!axios.isAxiosError(error) || error.response?.status !== 401) {
        throw error;
      }
      user.value = null;
    }
    isInitialized.value = true;
  };

  const signOut = async () => {
    await logout();
    user.value = null;
  };

  const canManageUsers = computed(
    () => user.value?.permissions.includes('users.manage') ?? false,
  );

  const canManageRateCard = computed(
    () => user.value?.permissions.includes('rate_cards.manage') ?? false,
  );

  const hasPermission = (permission: AuthUser['permissions'][number]) =>
    computed(() => user.value?.permissions.includes(permission) ?? false);
  const canApproveDiscounts = hasPermission('discounts.approve');
  const canCountersign = hasPermission('order_forms.countersign');
  const canViewAllOrderForms = hasPermission('order_forms.view_all');
  const canReassignOrderForms = hasPermission('order_forms.reassign');
  const canManageTemplates = hasPermission('templates.manage');
  const canManageBilling = hasPermission('billing.manage');
  const canViewAllCommissions = hasPermission('commissions.view_all');
  const canManageCommissions = hasPermission('commissions.manage');
  const canApproveStatements = hasPermission('commission_statements.approve');
  const canViewFinancials = hasPermission('financials.view');
  const canGrantFinancials = hasPermission('financials.grant');

  return {
    user,
    initialize,
    signOut,
    canManageUsers,
    canManageRateCard,
    canApproveDiscounts,
    canCountersign,
    canViewAllOrderForms,
    canReassignOrderForms,
    canManageTemplates,
    canManageBilling,
    canViewAllCommissions,
    canManageCommissions,
    canApproveStatements,
    canViewFinancials,
    canGrantFinancials,
  };
});
